import { Component } from '@theme/component';

/**
 * Vendor brand directory with client-side search and sorting.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLInputElement} searchInput
 * @property {HTMLDetailsElement} sortDetails
 * @property {HTMLElement} sortStatus
 * @property {HTMLElement} resultsCount
 * @property {HTMLElement} emptyState
 *
 * @extends {Component<Refs>}
 */
class BrandVendorListComponent extends Component {
  requiredRefs = ['grid', 'searchInput', 'sortDetails', 'sortStatus', 'resultsCount', 'emptyState'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleDocumentClick);

    this.#updateSortStatus();
    this.#updateSelectedOptionState();
    this.#applyFiltersAndSorting();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  get brandItems() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-brand-item]')));
  }

  get currentSortInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="brand-sort"]:checked'));
  }

  focusSearch = () => {
    this.refs.searchInput.focus();
  };

  filterBrands = () => {
    this.#applyFiltersAndSorting();
  };

  /** @param {Event} event */
  onSortChange = (event) => {
    const target = this.#getInteractiveTarget(event);
    if (!(target instanceof HTMLInputElement) || target.name !== 'brand-sort') return;

    this.#updateSortStatus(target);
    this.#updateSelectedOptionState();
    this.#closeSortMenu();
    this.#applyFiltersAndSorting();
  };

  onSortToggle = () => {
    const { sortDetails } = this.refs;
    const summary = sortDetails.querySelector('summary');
    const isOpen = sortDetails.open;

    summary?.setAttribute('aria-expanded', isOpen.toString());

    if (!isOpen) return;

    const selectedOption = sortDetails.querySelector('[role="option"][aria-selected="true"]');
    if (selectedOption instanceof HTMLElement) selectedOption.focus();
  };

  /** @param {KeyboardEvent} event */
  onKeyDown = (event) => {
    const { sortDetails } = this.refs;
    const target = this.#getInteractiveTarget(event);
    if (!(target instanceof Element) || !sortDetails.open || !sortDetails.contains(target)) return;

    const options = Array.from(sortDetails.querySelectorAll('[role="option"]'));
    if (options.length === 0) return;

    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;
      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;
      case 'Enter':
      case ' ': {
        const targetOption = target.closest('[role="option"]');
        if (!(targetOption instanceof HTMLElement)) return;

        event.preventDefault();
        this.#selectOption(targetOption);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.#closeSortMenu();
        break;
    }
  };

  #applyFiltersAndSorting() {
    const query = this.refs.searchInput.value.trim().toLowerCase();
    const sortValue = this.currentSortInput?.value ?? 'alphabetical';
    const sortedItems = [...this.brandItems].sort((itemA, itemB) => this.#compareItems(itemA, itemB, sortValue));
    const fragment = document.createDocumentFragment();
    let visibleCount = 0;

    for (const item of sortedItems) {
      const searchableName = item.dataset.brandSearch ?? item.dataset.brandSort ?? '';
      const matchesQuery = query === '' || searchableName.includes(query);

      item.hidden = !matchesQuery;
      item.setAttribute('aria-hidden', String(!matchesQuery));

      if (matchesQuery) visibleCount += 1;

      fragment.appendChild(item);
    }

    this.refs.grid.appendChild(fragment);
    this.refs.emptyState.hidden = visibleCount > 0;
    this.#updateCount(visibleCount);
  }

  /**
   * @param {HTMLElement} itemA
   * @param {HTMLElement} itemB
   * @param {string} sortValue
   */
  #compareItems(itemA, itemB, sortValue) {
    const brandA = itemA.dataset.brandSort ?? '';
    const brandB = itemB.dataset.brandSort ?? '';
    const direction = sortValue === 'alphabetical_reversed' ? -1 : 1;

    return brandA.localeCompare(brandB, undefined, { numeric: true, sensitivity: 'base' }) * direction;
  }

  #updateSortStatus(input = this.currentSortInput) {
    if (!(input instanceof HTMLInputElement)) return;

    this.refs.sortStatus.textContent = input.dataset.optionName ?? input.value;
  }

  #updateSelectedOptionState() {
    const options = this.querySelectorAll('.brand-vendor-list__sort-option');

    for (const option of options) {
      if (!(option instanceof HTMLElement)) continue;

      const input = option.querySelector('input[type="radio"]');
      const isChecked = input instanceof HTMLInputElement && input.checked;

      option.setAttribute('aria-selected', String(isChecked));
      option.tabIndex = isChecked ? 0 : -1;
    }
  }

  /** @param {number} count */
  #updateCount(count) {
    const singular = this.dataset.countSingular ?? 'brand';
    const plural = this.dataset.countPlural ?? 'brands';
    const label = count === 1 ? singular : plural;

    this.refs.resultsCount.textContent = `${count} ${label}`;
  }

  /** @param {Element[]} options @param {number} newFocusIndex */
  #moveFocus(options, newFocusIndex) {
    for (const [index, option] of options.entries()) {
      if (!(option instanceof HTMLElement)) continue;
      option.tabIndex = index === newFocusIndex ? 0 : -1;
    }

    const nextOption = options[newFocusIndex];
    if (nextOption instanceof HTMLElement) nextOption.focus();
  }

  /** @param {Event} event */
  #getInteractiveTarget(event) {
    return event.composedPath?.()[0] ?? event.target;
  }

  /** @param {Event} event */
  #handleDocumentClick = (event) => {
    const { sortDetails } = this.refs;
    const target = this.#getInteractiveTarget(event);

    if (!(sortDetails instanceof HTMLDetailsElement) || sortDetails.open === false) return;
    if (target instanceof Node && sortDetails.contains(target)) return;

    this.#closeSortMenu(false);
  };

  /** @param {HTMLElement} option */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {boolean} [focusSummary] */
  #closeSortMenu(focusSummary = true) {
    const { sortDetails } = this.refs;
    const summary = sortDetails.querySelector('summary');

    sortDetails.open = false;
    summary?.setAttribute('aria-expanded', 'false');
    if (focusSummary) summary?.focus();
  }
}

if (!customElements.get('brand-vendor-list-component')) {
  customElements.define('brand-vendor-list-component', BrandVendorListComponent);
}